import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Heart, 
  BookOpen, 
  Gem, 
  Shirt, 
  Gift, 
  Sparkles,
  MapPin,
  Mail,
  Phone,
  ExternalLink,
  HandHeart,
  Users,
  Home,
  Star
} from 'lucide-react';

const Donate = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const donationCategories = [
    {
      id: 'books',
      icon: BookOpen,
      title: 'Books',
      description: 'New or gently used books for all ages. Children&apos;s books, novels, educational materials, and more.',
      examples: ['Children&apos;s Books', 'Fiction & Non-Fiction', 'Educational Books', 'Spiritual & Self-Help'],
      color: 'bg-amber-500'
    },
    {
      id: 'crystals',
      icon: Gem,
      title: 'Crystals & Stones',
      description: 'Healing crystals, gemstones, and mineral specimens to share the magic of nature.',
      examples: ['Tumbled Stones', 'Raw Crystals', 'Crystal Jewelry', 'Mineral Specimens'],
      color: 'bg-purple-500'
    },
    {
      id: 'toys',
      icon: Gift,
      title: 'Toys & Games',
      description: 'New or gently used toys, puzzles, and games that bring joy to children.',
      examples: ['Board Games', 'Puzzles', 'Educational Toys', 'Stuffed Animals'],
      color: 'bg-pink-500'
    },
    {
      id: 'clothes',
      icon: Shirt,
      title: 'Clothing',
      description: 'Clean, gently used clothing for children and adults in our community.',
      examples: ['Children&apos;s Clothing', 'Adult Apparel', 'Accessories', 'Seasonal Items'],
      color: 'bg-teal-500'
    },
    {
      id: 'other',
      icon: Sparkles,
      title: 'Other Items',
      description: 'Soaps, candles, incense, home goods, and other treasures that nurture the spirit.',
      examples: ['Handmade Soaps', 'Candles & Incense', 'Home Decor', 'Craft Supplies'],
      color: 'bg-orange-500'
    }
  ];

  const impactStats = [
    { number: '500+', label: 'Books Donated', icon: BookOpen },
    { number: '200+', label: 'Families Helped', icon: Users },
    { number: '1', label: 'Community Space', icon: Home },
    { number: '∞', label: 'Stories Shared', icon: Heart },
  ];

  return (
    <div className="min-h-screen" data-testid="donate-page">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50 via-orange-50/50 to-background" />
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1370295/pexels-photo-1370295.jpeg')] bg-cover bg-center opacity-10" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-primary/10 rounded-full">
              <Heart className="h-4 w-4 text-primary fill-primary" />
              <span className="text-sm font-medium text-primary">Support Our Mission</span>
            </div>

            {/* Main heading */}
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              <span className="text-primary">Keep Books Alive</span>
            </h1>

            {/* Mission statement */}
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
              We&apos;re building a space where kids and families can reconnect with reading. 
              Your support helps keep our doors open and books in hands. 
              <span className="font-semibold text-foreground"> Every donation counts!</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a 
                href="https://gofund.me/52fff8c6c" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="btn-accent w-full sm:w-auto text-lg px-8 py-6" data-testid="gofundme-btn">
                  <HandHeart className="h-5 w-5 mr-2" />
                  Donate on GoFundMe
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </a>
              <a href="#item-donations">
                <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6">
                  <Gift className="h-5 w-5 mr-2" />
                  Donate Items
                </Button>
              </a>
            </div>

            {/* Location */}
            <div className="inline-flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>Hickory, NC</span>
              <span className="mx-2">•</span>
              <span className="text-primary font-medium">BrookzoneBookseller.com</span>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 bg-primary/5 border-y border-primary/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {impactStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
                  <stat.icon className="h-6 w-6 text-primary" />
                </div>
                <p className="font-serif text-3xl font-bold text-foreground">{stat.number}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">Our Story</Badge>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-6">
                More Than a Bookstore — <span className="text-primary">A Community Sanctuary</span>
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Brookzone Bookseller was born from a simple belief: every child deserves access to books, 
                  and every family deserves a place to nurture their love of reading. Located in the heart 
                  of Hickory, North Carolina, we&apos;re creating more than a store — we&apos;re building a sanctuary 
                  for curious minds.
                </p>
                <p>
                  Our shelves are filled with stories waiting to inspire, crystals charged with positive 
                  energy, and treasures that spark joy. But we can&apos;t do this alone. With your generous 
                  donations, we can continue providing affordable books to families, hosting reading events 
                  for children, and maintaining a welcoming space where the community can gather.
                </p>
                <p className="font-medium text-foreground">
                  Every book donated is a story passed on. Every dollar contributed keeps our lights on 
                  and our doors open. Together, we keep books alive.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/1370298/pexels-photo-1370298.jpeg" 
                  alt="Cozy bookstore interior"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating card */}
              <Card className="absolute -bottom-6 -left-6 sm:-left-12 max-w-xs shadow-xl">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Star className="h-5 w-5 text-primary fill-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-foreground">Community Driven</p>
                      <p className="text-xs text-muted-foreground">Built by readers, for readers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Item Donations Section */}
      <section id="item-donations" className="py-16 sm:py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">Item Donations</Badge>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
              What We Accept
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Your gently used items find new life in our community. Here&apos;s what we&apos;re collecting:
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {donationCategories.map((category) => (
              <Card 
                key={category.id}
                className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
                  selectedCategory === category.id ? 'ring-2 ring-primary shadow-lg' : ''
                }`}
                onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                data-testid={`donation-category-${category.id}`}
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl ${category.color} flex items-center justify-center mb-4`}>
                    <category.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-foreground mb-2">{category.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{category.description}</p>
                  
                  {selectedCategory === category.id && (
                    <div className="pt-4 border-t border-border animate-fade-in">
                      <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Examples:</p>
                      <div className="flex flex-wrap gap-2">
                        {category.examples.map((example, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {example}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Drop-off Info */}
          <Card className="mt-12 bg-primary/5 border-primary/20">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-serif text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Drop-Off Location
                  </h3>
                  <p className="text-muted-foreground mb-2">Brookzone Bookseller</p>
                  <p className="text-muted-foreground mb-4">Hickory, NC</p>
                  <p className="text-sm text-muted-foreground">
                    Please call ahead for drop-off times and large donations.
                  </p>
                </div>
                <div>
                  <h3 className="font-serif text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    Contact Us
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    Questions about donating? We&apos;d love to hear from you!
                  </p>
                  <Link to="/contact">
                    <Button variant="outline" className="mt-2">
                      <Mail className="h-4 w-4 mr-2" />
                      Send a Message
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Monetary Donations CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary/10 via-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-6">
            <HandHeart className="h-8 w-8 text-primary" />
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Prefer to Donate Funds?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Your financial contributions help us purchase new inventory, maintain our space, 
            and continue serving the Hickory community. Every dollar makes a difference.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://gofund.me/52fff8c6c" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg" className="btn-accent w-full sm:w-auto text-lg px-8 py-6">
                <Heart className="h-5 w-5 mr-2 fill-current" />
                Support on GoFundMe
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>100% goes to the cause</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>Secure donations</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>Tax deductible</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 bg-foreground text-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="font-serif text-2xl sm:text-3xl italic mb-6 opacity-90">
            &ldquo;A book is a gift you can open again and again.&rdquo;
          </blockquote>
          <p className="text-sm opacity-70">— Garrison Keillor</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-8 rounded-2xl bg-muted/50">
            <div>
              <h3 className="font-serif text-xl font-bold text-foreground mb-1">
                Ready to make a difference?
              </h3>
              <p className="text-muted-foreground">
                Join us in keeping the magic of reading alive in our community.
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/contact">
                <Button variant="outline">Contact Us</Button>
              </Link>
              <a href="https://gofund.me/52fff8c6c" target="_blank" rel="noopener noreferrer">
                <Button className="btn-accent">
                  <Heart className="h-4 w-4 mr-2" />
                  Donate Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
